/**
 * /docs/onboarding-a-trust — Step-by-step guide to onboarding a trust.
 */

import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect } from 'react';
import { LegalLayout } from '../../components/layouts/legal-layout';

export const Route = createFileRoute('/docs/onboarding-a-trust')({
  component: OnboardingTrustPage,
});

const TOC = [
  { anchor: 'overview',        label: 'Overview of the flow' },
  { anchor: 'particulars',     label: '1 · Trust particulars' },
  { anchor: 'trustees',        label: '2 · Trustee panel' },
  { anchor: 'beneficiaries',   label: '3 · Beneficiary register' },
  { anchor: 'assets',          label: '4 · Initial asset schedule' },
  { anchor: 'fica',            label: '5 · FICA collection per BO' },
  { anchor: 'risk-rating',     label: '6 · Risk rating' },
  { anchor: 'lodgement',       label: '7 · Lodgement with the Master' },
];

function OnboardingTrustPage() {
  useEffect(() => {
    document.title = 'Onboarding a trust · Elreign documentation';
    const meta = document.querySelector('meta[name="description"]') ?? (() => {
      const m = document.createElement('meta');
      m.name = 'description';
      document.head.appendChild(m);
      return m;
    })();
    meta.setAttribute(
      'content',
      'How to onboard a South African trust on Elreign: from deed reference through to FICA-collected beneficial owners, risk rating, and Master ICMS lodgement.',
    );
  }, []);

  return (
    <LegalLayout heading="Onboarding a trust." lastUpdated="17 May 2026" toc={TOC}>
      <p>
        Onboarding moves a trust from a deed in a filing cabinet to a live record on Elreign with
        every beneficial owner FICA-collected, risk rating computed, and the platform ready to
        draft the first resolution. Allow 90 minutes for the first trust.
      </p>

      <h2 id="overview">Overview of the flow</h2>
      <p>
        The wizard runs in seven sequential steps. You can save and resume at any point; partial
        state is held in the principal administrator&apos;s draft store and visible only to that
        administrator until the trust is provisioned.
      </p>

      <h2 id="particulars">1 · Trust particulars</h2>
      <p>
        Capture the trust name as it appears on the deed, the Master&apos;s registration number
        (IT or TM prefix as applicable), the date of registration, and the jurisdiction. Upload
        the executed deed and any amendments. The platform parses the deed into structured fields
        (trustees, beneficiaries, powers, distribution conditions) and presents them for your
        confirmation. Originals are retained in the document vault under WORM immutability for
        the lifetime of the trust.
      </p>

      <h2 id="trustees">2 · Trustee panel</h2>
      <p>
        Confirm the trustees named on the deed. For each trustee, capture the identification
        number, contact details, and the role (independent trustee, founding trustee, professional
        trustee). The platform creates a signing channel for each trustee and sends an out-of-band
        verification SMS to register the contact.
      </p>

      <h2 id="beneficiaries">3 · Beneficiary register</h2>
      <p>
        Capture each beneficiary on the deed. Beneficiaries are typed as named, class-defined, or
        contingent. For each named beneficiary, capture identification, contact details, and
        nominated bank account for distributions. Class-defined beneficiaries (e.g. &quot;all
        descendants of X&quot;) are recorded as classes; individual members are added as they
        become identifiable.
      </p>

      <h2 id="assets">4 · Initial asset schedule</h2>
      <p>
        Capture the opening asset schedule. Assets are typed as property, equities, cash,
        unit-linked, alternative, and other. For each asset, capture description, valuation
        method, opening value, and any encumbrance. The schedule becomes the baseline for all
        subsequent statements and distributions.
      </p>

      <h2 id="fica">5 · FICA collection per beneficial owner</h2>
      <p>
        For each beneficial owner (trustees, named beneficiaries, founders)  the
        wizard prompts for FICA documents per the FIC Act: proof of identity, proof of address
        not older than three months, and source-of-funds declaration. Documents are scanned or
        photographed and uploaded directly. Sanctions and PEP screening is performed by your firm
        as the accountable institution; Elreign records the screening outcome per beneficial owner
        (list checked, date, result and reviewer) with timestamp on the audit chain. Direct
        integration with screening list providers is on the product roadmap and is not yet live.
      </p>

      <h2 id="risk-rating">6 · Risk rating</h2>
      <p>
        Risk rating is computed against your firm&apos;s policy thresholds: geography of
        beneficiaries, source of funds, complexity of asset structure, PEP exposure, and any
        adverse media. The platform proposes a rating; you may override with a written
        justification that is sealed on the audit chain. The trust is then assigned a review
        cadence (annual, two-yearly, or three-yearly) per the rating.
      </p>

      <h2 id="lodgement">7 · Lodgement with the Master</h2>
      <p>
        If the trust is newly registered, the platform produces the Master&apos;s lodgement
        package (J401, beneficial ownership register, deed, FICA evidence per BO) and queues it
        for principal administrator review. Authorise the package and lodge from inside the
        platform. The Master&apos;s receipt number is captured against the trust and sealed on
        the audit chain when received.
      </p>

      <p style={{ marginTop: 32 }}>
        <Link to="/docs">Back to documentation hub →</Link>
      </p>
    </LegalLayout>
  );
}
